import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Activity, 
  Calendar, 
  Users, 
  FileText, 
  Plus, 
  Search, 
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Pill,
  Stethoscope,
  Shield,
  Phone,
  MapPin,
  Download,
  Share2,
  Edit,
  Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface HealthProvider {
  id: string;
  name: string;
  specialty?: string;
  practice_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
  is_primary: boolean;
  created_at: string;
}

interface MedicalRecord {
  id: string;
  date: string;
  type: string;
  provider_id?: string;
  diagnosis?: string;
  treatment?: string;
  medications?: string[];
  test_results?: any;
  notes?: string;
  attachments?: string[];
  created_at: string;
}

interface Appointment {
  id: string;
  provider_id?: string;
  date: string;
  time?: string;
  type: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  notes?: string;
  location?: string;
  created_at: string;
}

interface EmergencyContact {
  id: string;
  name: string;
  relationship?: string;
  phone: string;
  phone_secondary?: string;
  email?: string;
  address?: string;
  is_primary: boolean;
  notes?: string;
  created_at: string;
}

interface HealthOverviewStats {
  totalProviders: number;
  primaryProviders: number;
  totalRecords: number;
  upcomingAppointments: number;
  emergencyContacts: number;
  healthScore: number;
  lastCheckup: string | null;
  nextAppointment: string | null;
}

export function MobileHealthOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'providers' | 'records' | 'appointments' | 'emergency'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Handle navigation to specific sections
  const handleNavigateToSection = (section: string) => {
    navigate(`/mobile/health/${section}`);
  };

  // Handle quick action navigation
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'records':
        navigate('/mobile/health/records');
        break;
      case 'medications':
        navigate('/mobile/health/medications');
        break;
      case 'appointments':
        navigate('/mobile/health/appointments');
        break;
      case 'emergency':
        navigate('/mobile/health/emergency');
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Handle button actions
  const handleViewProvider = (providerId: string) => {
    console.log('View provider:', providerId);
  };

  const handleCallProvider = (providerId: string) => {
    console.log('Call provider:', providerId);
  };

  const handleEditProvider = (providerId: string) => {
    console.log('Edit provider:', providerId);
  };

  const handleDownloadRecord = (recordId: string) => {
    console.log('Download record:', recordId);
  };

  const handleEditRecord = (recordId: string) => {
    console.log('Edit record:', recordId);
  };

  const handleEditAppointment = (appointmentId: string) => {
    console.log('Edit appointment:', appointmentId);
  };

  const handleCallEmergency = (contactId: string) => {
    console.log('Call emergency contact:', contactId);
  };

  const handleEditEmergency = (contactId: string) => {
    console.log('Edit emergency contact:', contactId);
  };

  const handleAddNew = () => {
    console.log('Add new health item');
  };

  // Data states
  const [providers, setProviders] = useState<HealthProvider[]>([]);
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([]);
  const [stats, setStats] = useState<HealthOverviewStats>({
    totalProviders: 0,
    primaryProviders: 0,
    totalRecords: 0,
    upcomingAppointments: 0,
    emergencyContacts: 0,
    healthScore: 0,
    lastCheckup: null,
    nextAppointment: null
  });

  // Load data from database
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Load health providers
        const { data: providersData } = await supabase
          .from('health_providers')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        setProviders(providersData || []);

        // Load medical records
        const { data: recordsData } = await supabase
          .from('medical_records')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false });

        setMedicalRecords(recordsData || []);

        // Load appointments
        const { data: appointmentsData } = await supabase
          .from('health_appointments')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: true });

        setAppointments(appointmentsData || []);

        // Load emergency contacts
        const { data: contactsData } = await supabase
          .from('emergency_contacts')
          .select('*')
          .eq('user_id', user.id)
          .order('is_primary', { ascending: false });

        setEmergencyContacts(contactsData || []);

        // Calculate stats
        const primaryProviders = providers.filter(p => p.is_primary).length;
        const upcomingAppointments = appointments.filter(a => 
          a.status === 'scheduled' && new Date(a.date) > new Date()
        ).length;
        
        const lastCheckup = medicalRecords
          .filter(r => r.type === 'checkup')
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]?.date || null;
        
        const nextAppointment = appointments
          .filter(a => a.status === 'scheduled' && new Date(a.date) > new Date())
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0]?.date || null;

        const healthScore = Math.min(100, 
          (primaryProviders * 20) + 
          (upcomingAppointments > 0 ? 20 : 0) + 
          (emergencyContacts.length > 0 ? 20 : 0) + 
          (medicalRecords.length > 0 ? 20 : 0) +
          (lastCheckup ? 20 : 0)
        );

        setStats({
          totalProviders: providers.length,
          primaryProviders,
          totalRecords: medicalRecords.length,
          upcomingAppointments,
          emergencyContacts: emergencyContacts.length,
          healthScore,
          lastCheckup,
          nextAppointment
        });

      } catch (err) {
        console.error('Error loading health data:', err);
        setError('Failed to load health data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'text-blue-600 bg-blue-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string, timeString: string) => {
    const date = new Date(`${dateString}T${timeString}`);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Health Score */}
      <Card className="p-4 bg-gradient-to-r from-red-500 to-pink-500">
        <div className="text-center text-white">
          <h3 className="text-lg font-semibold mb-2">Health Score</h3>
          <div className="text-3xl font-bold mb-2">{stats.healthScore}/100</div>
          <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.healthScore}%` }}
            />
          </div>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Stethoscope size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Providers</p>
              <p className="text-lg font-semibold">{stats.totalProviders}</p>
              <p className="text-xs text-green-600">{stats.primaryProviders} primary</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FileText size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Records</p>
              <p className="text-lg font-semibold">{stats.totalRecords}</p>
              <p className="text-xs text-gray-600">Medical records</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Appointments</p>
              <p className="text-lg font-semibold">{stats.upcomingAppointments}</p>
              <p className="text-xs text-blue-600">Upcoming</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Phone size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Emergency</p>
              <p className="text-lg font-semibold">{stats.emergencyContacts}</p>
              <p className="text-xs text-gray-600">Contacts</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Next Appointment */}
      {stats.nextAppointment && (
        <Card className="p-4 bg-blue-50 border border-blue-200">
          <div className="flex items-center gap-3">
            <Calendar size={20} className="text-blue-600" />
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Next Appointment</h3>
              <p className="text-sm text-gray-600">{formatDate(stats.nextAppointment)}</p>
            </div>
            <Button size="sm" variant="outline">
              View
            </Button>
          </div>
        </Card>
      )}

      {/* Last Checkup */}
      {stats.lastCheckup && (
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} className="text-green-600" />
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Last Checkup</h3>
              <p className="text-sm text-gray-600">{formatDate(stats.lastCheckup)}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => handleQuickAction('records')}
        >
          <FileText size={16} className="mr-2" />
          Records
        </Button>
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => handleQuickAction('medications')}
        >
          <Pill size={16} className="mr-2" />
          Medications
        </Button>
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => handleQuickAction('appointments')}
        >
          <Calendar size={16} className="mr-2" />
          Appointments
        </Button>
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => handleQuickAction('emergency')}
        >
          <Phone size={16} className="mr-2" />
          Emergency
        </Button>
      </div>
    </div>
  );

  const renderProviders = () => (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search providers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Provider List */}
      <div className="space-y-3">
        {providers
          .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       (p.specialty && p.specialty.toLowerCase().includes(searchQuery.toLowerCase())))
          .map((provider) => (
            <Card key={provider.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Stethoscope size={16} className="text-blue-500" />
                    <h3 className="font-medium text-gray-900">{provider.name}</h3>
                    {provider.is_primary && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                        Primary
                      </span>
                    )}
                  </div>
                  {provider.specialty && (
                    <p className="text-sm text-gray-600">{provider.specialty}</p>
                  )}
                  {provider.practice_name && (
                    <p className="text-xs text-gray-500">{provider.practice_name}</p>
                  )}
                  {provider.phone && (
                    <p className="text-xs text-gray-500">{provider.phone}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleCallProvider(provider.id)}
                  >
                    <Phone size={14} />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleEditProvider(provider.id)}
                  >
                    <Edit size={14} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
      </div>
    </div>
  );

  const renderRecords = () => (
    <div className="space-y-4">
      {/* Medical Records List */}
      <div className="space-y-3">
        {medicalRecords.map((record) => (
          <Card key={record.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={16} className="text-green-500" />
                  <h3 className="font-medium text-gray-900">{record.type}</h3>
                </div>
                <p className="text-sm text-gray-600">{record.type} • {record.diagnosis || 'No diagnosis'}</p>
                <p className="text-xs text-gray-500">{formatDate(record.date)}</p>
              </div>
              <div className="flex gap-1">
                {record.attachments && record.attachments.length > 0 && (
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleDownloadRecord(record.id)}
                  >
                    <Download size={14} />
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleEditRecord(record.id)}
                >
                  <Edit size={14} />
                </Button>
              </div>
            </div>
            {record.medications && record.medications.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {record.medications.map((medication, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                    {medication}
                  </span>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );

  const renderAppointments = () => (
    <div className="space-y-4">
      {/* Appointments List */}
      <div className="space-y-3">
        {appointments.map((appointment) => (
          <Card key={appointment.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar size={16} className="text-purple-500" />
                  <h3 className="font-medium text-gray-900">{appointment.type}</h3>
                </div>
                {appointment.provider_id && (
                  <p className="text-sm text-gray-600">{appointment.provider_id}</p>
                )}
                <p className="text-xs text-gray-500">
                  {appointment.time ? formatDateTime(appointment.date, appointment.time) : formatDate(appointment.date)}
                </p>
                {appointment.location && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin size={12} />
                    {appointment.location}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(appointment.status)}`}>
                  {appointment.status}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleEditAppointment(appointment.id)}
                >
                  <Edit size={14} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderEmergency = () => (
    <div className="space-y-4">
      {/* Emergency Contacts List */}
      <div className="space-y-3">
        {emergencyContacts.map((contact) => (
          <Card key={contact.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Phone size={16} className="text-red-500" />
                  <h3 className="font-medium text-gray-900">{contact.name}</h3>
                  {contact.is_primary && (
                    <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                      Primary
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{contact.relationship || 'Contact'}</p>
                <p className="text-xs text-gray-500">{contact.phone}</p>
                {contact.email && (
                  <p className="text-xs text-gray-500">{contact.email}</p>
                )}
              </div>
              <div className="flex gap-1">
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleCallEmergency(contact.id)}
                >
                  <Phone size={14} />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleEditEmergency(contact.id)}
                >
                  <Edit size={14} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Health Management</h2>
          <p className="text-sm text-gray-600">Manage providers, records, and appointments</p>
        </div>
        <Button size="sm" onClick={handleAddNew}>
          <Plus size={16} className="mr-2" />
          Add
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto">
        <Button
          variant={activeTab === 'overview' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('overview')}
          className="whitespace-nowrap"
        >
          Overview
        </Button>
        <Button
          variant={activeTab === 'providers' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('providers')}
          className="whitespace-nowrap"
        >
          Providers
        </Button>
        <Button
          variant={activeTab === 'records' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('records')}
          className="whitespace-nowrap"
        >
          Records
        </Button>
        <Button
          variant={activeTab === 'appointments' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('appointments')}
          className="whitespace-nowrap"
        >
          Appointments
        </Button>
        <Button
          variant={activeTab === 'emergency' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('emergency')}
          className="whitespace-nowrap"
        >
          Emergency
        </Button>
      </div>

      {/* Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'providers' && renderProviders()}
      {activeTab === 'records' && renderRecords()}
      {activeTab === 'appointments' && renderAppointments()}
      {activeTab === 'emergency' && renderEmergency()}
    </div>
  );
} 